import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  FormControlLabel,
  Switch,
  LinearProgress,
  Fade,
  Divider,
} from '@mui/material'
import CampaignIcon from '@mui/icons-material/Campaign'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import EditNoteIcon from '@mui/icons-material/EditNote'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { useTts } from '../../contexts/TtsContext'
import { synthesize } from '../../services/ttsApi'
import { useAuth } from '../../App'
import CharacterCounter from './CharacterCounter'
import SampleTextSelector from './SampleTextSelector'
import TtsAudioPlayer from './TtsAudioPlayer'
import TtsMetricsPanel from './TtsMetricsPanel'
import TtsStreamingMode from './TtsStreamingMode'
import LanguagePicker from './LanguagePicker'
import VoicePicker from './VoicePicker'
import { useTtsLanguages } from '../../hooks/useTtsLanguages'
import {
  voicesForLanguage as pickVoicesForLanguage,
  filterVoices,
  reconcileSelectedVoice,
  resolveSynthesisLanguage,
  voiceLanguageBucket,
} from '../../types/tts'
import { tokens } from '../../theme/tokens'

const MAX_CHARS = 5000

interface TtsPlaygroundProps {
  text: string
  setText: (text: string) => void
  selectedVoiceId: string
  setSelectedVoiceId: (id: string) => void
}

interface Metrics {
  ttfbMs: number
  totalTimeMs: number
  charactersProcessed: number
  requestId?: string
  audioDurationS?: number
}

// Language-first TTS flow (catalog v4): choose a language → pick one of that
// language's native voices → synthesize. Stock voices are per-language; cloned
// voices live in the separate "My Voices" space.
const TtsPlayground: React.FC<TtsPlaygroundProps> = ({
  text,
  setText,
  selectedVoiceId,
  setSelectedVoiceId,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { voices, voicesLoading, voicesError, loadVoices, checkTtsHealth } = useTts()
  const auth = useAuth()
  const location = useLocation()

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [streamingMode, setStreamingMode] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('')
  // Gender/vocal-range filters are lifted here (out of VoicePicker) so the parent
  // knows the visible voice list and can keep `selectedVoiceId` on a VISIBLE
  // voice. Vocal range is a FILTER over the language's voices, not a synthesis
  // parameter: each voice has exactly one range, and it's a property of the voice.
  const [genderFilter, setGenderFilter] = useState('all')
  const [rangeFilter, setRangeFilter] = useState('')

  const initializedRef = useRef(false)

  // Stock voices only — cloned voices are browsed in the separate "My Voices" space.
  const stockVoices = useMemo(() => voices.filter((v) => !v.isCloned), [voices])

  // Shared language plumbing (merge + orphan bucket + has-voices-first default).
  const { languageOptions, languagesLoading, preferredLanguage } = useTtsLanguages(stockVoices)

  // Orphan voices (unresolved language) live under the synthetic 'unknown' chip;
  // the shared helper normalizes so they're reachable when that chip is selected.
  const voicesForLanguage = useMemo(
    () => pickVoicesForLanguage(stockVoices, selectedLanguage),
    [stockVoices, selectedLanguage]
  )

  // Voices visible after the gender/range filters — the set the user can actually
  // pick from, and the set the selection must stay within. Range matching goes
  // through the voice's FULL pitch list (shared helper), so a legacy multi-pitch
  // voice stays visible under every range it can actually be synthesized at.
  const visibleVoices = useMemo(
    () => filterVoices(voicesForLanguage, { gender: genderFilter, range: rangeFilter }),
    [voicesForLanguage, genderFilter, rangeFilter]
  )

  const selectedLangOption = languageOptions.find((l) => l.code === selectedLanguage)
  const selectedVoice = useMemo(
    () => voices.find((v) => v.voice_id === selectedVoiceId),
    [voices, selectedVoiceId]
  )
  // The active selection when it's a (zero-shot) cloned voice — shown as a banner
  // since cloned voices don't appear in the language's stock voice list.
  const selectedClonedVoice = selectedVoice?.isCloned ? selectedVoice : undefined
  // EVERY supported language streams; the service advertises it per language and
  // an unknown language is assumed streamable rather than blocked. `lowLatency`
  // is latency information only — never a capability gate.
  const streamingSupported = selectedLangOption?.streaming ?? true
  const lowLatency = Boolean(selectedLangOption?.lowLatency)

  // Load voices + health on mount.
  useEffect(() => {
    const init = async () => {
      try {
        const token = await auth.getToken()
        await checkTtsHealth()
        await loadVoices(token)
      } catch {
        // auth error handled by auth context
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initial selection: honor ?voice=<id> (jump to that voice + its language), else
  // default to a language that actually has voices (shared hook ranking).
  useEffect(() => {
    if (initializedRef.current) return
    if (voices.length === 0) return

    const params = new URLSearchParams(location.search)
    const voiceParam = params.get('voice')
    const paramVoice = voiceParam ? voices.find((v) => v.voice_id === voiceParam) : undefined
    if (paramVoice) {
      setSelectedLanguage(voiceLanguageBucket(paramVoice))
      setSelectedVoiceId(paramVoice.voice_id)
      initializedRef.current = true
      return
    }

    if (!preferredLanguage) return
    setSelectedLanguage(preferredLanguage)
    initializedRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voices, preferredLanguage, location.search])

  // Reset the gender/range filters when the language (voice set) changes.
  useEffect(() => {
    setGenderFilter('all')
    setRangeFilter('')
  }, [selectedLanguage])

  const toggleGender = useCallback(
    (value: string) => setGenderFilter((prev) => (prev === value ? 'all' : value)),
    []
  )
  const toggleRange = useCallback(
    (value: string) => setRangeFilter((prev) => (prev === value ? '' : value)),
    []
  )

  // Keep the selection on a VISIBLE voice. The rule itself lives in the shared,
  // unit-tested `reconcileSelectedVoice` so the invariant "a filtered-out voice is
  // never the one synthesized" is guarded by a test rather than by this effect.
  useEffect(() => {
    if (!selectedLanguage) return
    const next = reconcileSelectedVoice(selectedVoiceId, voices, visibleVoices)
    if (next !== null) setSelectedVoiceId(next)
  }, [voices, visibleVoices, selectedLanguage, selectedVoiceId, setSelectedVoiceId])

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value),
    [setText]
  )

  const handleSampleSelect = useCallback((sample: string) => setText(sample), [setText])

  const handleSelectLanguage = useCallback((code: string) => {
    setSelectedLanguage(code)
  }, [])

  // The language actually sent to the service. The synthetic 'unknown' bucket
  // isn't a language the service knows, so we fall back to the VOICE's own
  // declared language instead of sending nothing — sending nothing makes the
  // service apply its own default (en) to a voice that may not be English.
  // `undefined` therefore means the voice genuinely declares no language, which
  // the UI says out loud rather than letting the default happen silently.
  const synthesisLanguage = useMemo(
    () => resolveSynthesisLanguage(selectedLanguage, selectedVoice),
    [selectedLanguage, selectedVoice]
  )
  const languageUnresolved = Boolean(selectedVoiceId) && !synthesisLanguage

  const handleGenerate = useCallback(async () => {
    if (!text.trim() || !selectedVoiceId) return
    setLoading(true)
    setError(null)
    setAudioBlob(null)
    setMetrics(null)

    try {
      const token = await auth.getToken()
      const result = await synthesize(
        // The catalog voice_id is used VERBATIM — never derived from the name.
        selectedVoiceId,
        {
          text,
          format: 'wav',
          language: synthesisLanguage,
        },
        token
      )
      setAudioBlob(result.audioBlob)
      setMetrics({
        ttfbMs: result.ttfbMs,
        totalTimeMs: result.totalTimeMs,
        charactersProcessed: result.charactersProcessed,
        requestId: result.requestId,
        audioDurationS: result.audioDurationS,
      })
    } catch (err: any) {
      const msg: string = err.message || t('tts.errorGeneric', 'Failed to generate audio')
      if (msg.includes('402') || msg.toLowerCase().includes('insufficient balance')) {
        setError(
          t(
            'tts.error402',
            'Insufficient balance. Please top up your wallet via the billing menu to use TTS.'
          )
        )
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [text, selectedVoiceId, synthesisLanguage, auth, t])

  // Only allow generation when the selection is actually reachable: a cloned
  // voice (zero-shot) or a currently-visible stock voice. This closes the window
  // where a filtered-out voice_id lingers before reconciliation clears it.
  const isSelectionValid =
    !!selectedVoice &&
    (selectedVoice.isCloned || visibleVoices.some((v) => v.voice_id === selectedVoiceId))
  const canGenerate = text.trim().length > 0 && isSelectionValid && !loading
  const charCount = text.length
  const charPercent = (charCount / MAX_CHARS) * 100

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1.5, md: 2 },
        }}
      >
        {/* Header card -- title + streaming toggle */}
        <Box
          sx={{
            padding: { xs: 2, md: 3 },
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'background.paper',
            backdropFilter: 'blur(10px)',
            boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.06)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid',
            borderColor: isDark ? undefined : 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 4 },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: { xs: 'none', md: '1' } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: tokens.gradients.tts,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 0.5,
                fontSize: { xs: '1.8rem', md: '2.125rem' },
              }}
            >
              {t('tts.title', 'Text to Speech')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 400,
              }}
            >
              {t('tts.subtitle', 'Convert text to natural-sounding speech')}
            </Typography>
          </Box>

          {/* Streaming mode toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={streamingMode}
                  onChange={(e) => setStreamingMode(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <GraphicEqIcon
                    sx={{
                      fontSize: 18,
                      color: streamingMode
                        ? isDark
                          ? tokens.colors.tts.light
                          : tokens.colors.tts.main
                        : isDark
                          ? 'rgba(255,255,255,0.5)'
                          : 'text.secondary',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: streamingMode
                        ? isDark
                          ? tokens.colors.tts.light
                          : tokens.colors.tts.main
                        : isDark
                          ? 'rgba(255,255,255,0.7)'
                          : 'text.secondary',
                    }}
                  >
                    {t('tts.streamingMode', 'Streaming mode')}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, mr: 0 }}
            />
          </Box>
        </Box>

        {/* Voice catalog failed to load — otherwise the pickers look emptily broken. */}
        {voicesError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {t('tts.voicesError', 'Failed to load voices')}
          </Alert>
        )}

        {/* Streaming mode: no voice selected hint */}
        {streamingMode && !selectedVoiceId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {t('tts.selectVoiceFirst', 'Please select a voice to start streaming')}
          </Alert>
        )}

        {/* The selected voice declares no language and none was chosen, so no
            language can be sent. Say so — the service would otherwise silently
            fall back to English for a voice that may not be English. */}
        {languageUnresolved && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {t(
              'tts.unknownVoiceLanguage',
              "This voice's language is unknown, so the service will pick a default. Choose a language above for a reliable result."
            )}
          </Alert>
        )}

        {/* Workspace: language + voice pickers alongside the synthesize panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {/* Left panel -- language + voice selection */}
          <Paper
            sx={{
              width: { xs: '100%', md: 380 },
              flexShrink: 0,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider',
              borderRadius: 3,
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflow: 'auto',
            }}
          >
            <LanguagePicker
              languages={languageOptions}
              selectedLanguage={selectedLanguage}
              onSelectLanguage={handleSelectLanguage}
              loading={languagesLoading && languageOptions.length === 0}
            />
            <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }} />
            {/* A deep-linked cloned voice (zero-shot: speaks any language) isn't in
                the stock list — surface it so the selection is never invisible. */}
            {selectedClonedVoice && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                  background: isDark ? `${tokens.colors.tts.main}22` : tokens.colors.tts.surface,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t('tts.usingClonedVoice', {
                    defaultValue: 'Using your cloned voice: {{name}}',
                    name: selectedClonedVoice.displayName,
                  })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('tts.clonedAnyLanguage', 'Your voice can speak any language — pick one above.')}
                </Typography>
              </Box>
            )}
            <VoicePicker
              voices={voicesForLanguage}
              filteredVoices={visibleVoices}
              selectedVoiceId={selectedVoiceId}
              onSelectVoice={setSelectedVoiceId}
              genderFilter={genderFilter}
              rangeFilter={rangeFilter}
              onToggleGender={toggleGender}
              onToggleRange={toggleRange}
              loading={voicesLoading}
            />
          </Paper>

          {/* Right panel -- synthesize (REST) or live streaming */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
            {streamingMode ? (
              <Paper
                sx={{
                  flex: 1,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider',
                  borderRadius: 3,
                  p: 2.5,
                  overflow: 'auto',
                }}
              >
                <TtsStreamingMode
                  voiceId={selectedVoiceId}
                  text={text}
                  setText={setText}
                  language={synthesisLanguage ?? ''}
                  streamingSupported={streamingSupported}
                  lowLatency={lowLatency}
                />
              </Paper>
            ) : (
              <>
                {/* Text composition */}
                <Paper
                  sx={{
                    flex: 1,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 320, md: 0 },
                  }}
                >
                  {/* Text area header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid',
                      borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EditNoteIcon
                        sx={{ fontSize: 18, color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.tts.main,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontSize: '0.7rem',
                        }}
                      >
                        {t('tts.textLabel', 'Text to synthesize')}
                      </Typography>
                    </Box>
                    <SampleTextSelector onSelect={handleSampleSelect} />
                  </Box>

                  {/* Text input */}
                  <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      multiline
                      minRows={6}
                      maxRows={16}
                      fullWidth
                      value={text}
                      onChange={handleTextChange}
                      placeholder={t('tts.textPlaceholder', 'Enter text here...')}
                      inputProps={{
                        maxLength: MAX_CHARS,
                        'aria-label': t('tts.textLabel', 'Text to synthesize'),
                        'aria-describedby': 'tts-char-count',
                      }}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          height: '100%',
                          alignItems: 'flex-start',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: isDark ? 'rgba(255,255,255,0.9)' : 'text.primary',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                        },
                      }}
                    />

                    {/* Character count progress bar */}
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          id="tts-char-count"
                          variant="caption"
                          sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }}
                          aria-live="polite"
                        >
                          {charCount.toLocaleString()}/{MAX_CHARS.toLocaleString()}{' '}
                          {t('tts.characters', 'characters')}
                        </Typography>
                        <CharacterCounter current={charCount} max={MAX_CHARS} />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={charPercent}
                        sx={{
                          borderRadius: 1,
                          height: 3,
                          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                          '& .MuiLinearProgress-bar': {
                            background: tokens.gradients.tts,
                            borderRadius: 1,
                          },
                        }}
                      />
                    </Box>

                    {/* Generate CTA */}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleGenerate}
                        disabled={!canGenerate}
                        aria-busy={loading}
                        startIcon={
                          loading ? <CircularProgress size={18} color="inherit" /> : <CampaignIcon />
                        }
                        sx={{
                          borderRadius: 3,
                          py: 1.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '1rem',
                          transition: 'all 300ms ease',
                          background: canGenerate ? tokens.gradients.tts : undefined,
                          boxShadow: canGenerate
                            ? `0 4px 20px ${isDark ? 'rgba(0, 74, 87, 0.4)' : 'rgba(0, 74, 87, 0.25)'}`
                            : 'none',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${tokens.colors.tts.light} 0%, ${tokens.colors.tts.main} 100%)`,
                            boxShadow: `0 6px 28px ${isDark ? 'rgba(0, 74, 87, 0.5)' : 'rgba(0, 74, 87, 0.35)'}`,
                            transform: 'translateY(-1px)',
                          },
                          '&.Mui-disabled': {
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {loading
                          ? t('tts.generating', 'Generating...')
                          : t('tts.generateButton', 'Generate Speech')}
                      </Button>
                      {!canGenerate && !loading && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            mt: 1,
                            color: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary',
                            fontSize: '0.7rem',
                          }}
                        >
                          {!selectedVoiceId
                            ? t('tts.selectVoiceToGenerate', 'Please select a voice to generate audio')
                            : t('tts.enterTextHint', 'Enter text to enable generation')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Error */}
                {error && (
                  <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {/* Output zone -- audio player + metrics */}
                {(audioBlob || metrics) && (
                  <Fade in timeout={400}>
                    <Paper
                      sx={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid',
                        borderColor: isDark ? `${tokens.colors.tts.light}30` : `${tokens.colors.tts.main}20`,
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          borderBottom: '1px solid',
                          borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                          background: isDark
                            ? `linear-gradient(135deg, ${tokens.colors.tts.main}20 0%, transparent 100%)`
                            : `linear-gradient(135deg, ${tokens.colors.tts.surface} 0%, rgba(255,255,255,0) 100%)`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CampaignIcon
                            sx={{ fontSize: 18, color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.tts.main,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              fontSize: '0.7rem',
                            }}
                          >
                            {t('tts.output', 'Output')}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        {audioBlob && (
                          <Box sx={{ mb: metrics ? 2 : 0 }}>
                            <TtsAudioPlayer audioBlob={audioBlob} fileName="tts-audio.wav" />
                          </Box>
                        )}
                        {metrics && (
                          <TtsMetricsPanel
                            ttfbMs={metrics.ttfbMs}
                            totalTimeMs={metrics.totalTimeMs}
                            charactersProcessed={metrics.charactersProcessed}
                            requestId={metrics.requestId}
                            audioDurationS={metrics.audioDurationS}
                          />
                        )}
                      </Box>
                    </Paper>
                  </Fade>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  )
}

export default TtsPlayground
